import { OrbitControls } from '@react-three/drei';
import { useThree, useFrame } from '@react-three/fiber';
import { useEffect, useState, useRef, useMemo } from 'react';
import { useStore } from '../store';

function useOrientation() {
    const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  
    useEffect(() => {
      const handleResize = () => {
        setIsPortrait(window.innerHeight > window.innerWidth);
      };
  
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
  
    return isPortrait;
}

export default function CameraControls({ mobileSettings, desktopSettings }) {
    const { camera } = useThree();
    const controls = useRef();
    const isAnimating = useStore((state) => state.isAnimating);
    const setModelAnimationComplete = useStore((state) => state.setModelAnimationComplete);
    const [isRotating, setIsRotating] = useState(false);
    const startAngle = useRef(0);
    const elapsedTime = useRef(0);
    const wanderTime = useRef(0);
    const hasAnimated = useRef(false);
    const isPortrait = useOrientation();
    
    const baseRadius = isPortrait ? mobileSettings.cameraZ : desktopSettings.cameraZ;
    
    const springVelocity = useRef(0);
    const springTarget = useRef(0);
    const springCurrent = useRef(0);
  
    const easeInOutCubic = (x) => {
      return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
    };
  
    const easeOutBack = (x) => {
      const c1 = 1.20158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
    };
  
    const spring = useMemo(() => ({
      tension: 0.15,
      friction: 0.9,
      velocity: springVelocity,
      target: springTarget,
      current: springCurrent
    }), []); 
  
    useEffect(() => {
      if (isAnimating && !isRotating && !hasAnimated.current) {
        hasAnimated.current = true; 
        setTimeout(() => {
          setIsRotating(true);
          startAngle.current = Math.atan2(camera.position.z, camera.position.x);
          elapsedTime.current = 0;
          spring.current.current = startAngle.current;
          spring.target.current = startAngle.current + Math.PI * 2;
        }, 2000);
      }
    }, [isAnimating, camera, isRotating, spring]);
  
    useFrame((state, delta) => {
      if (isRotating) {
        elapsedTime.current += delta;
        const duration = 5;
        const progress = Math.min(elapsedTime.current / duration, 1);
  
        if (progress < 1) {
          const easedProgress = progress < 0.8 
            ? easeInOutCubic(progress / 0.8) 
            : easeOutBack((progress - 0.8) / 0.2);
            
          const angle = startAngle.current + (Math.PI * 2 * easedProgress);
          const heightOffset = Math.sin(progress * Math.PI) * 0.2;
          const radius = baseRadius + Math.sin(progress * Math.PI * 2) * 0.2;
          
          camera.position.x = radius * Math.cos(angle);
          camera.position.z = radius * Math.sin(angle);
          camera.position.y = heightOffset;
          
          const lookAtScale = Math.max(0, 1 - (progress * 2 - 1));
          const lookAtX = Math.sin(angle * 0.05) * 0.1 * lookAtScale;
          const lookAtZ = Math.cos(angle * 0.05) * 0.1 * lookAtScale;
          camera.lookAt(lookAtX, heightOffset * 0.3 * lookAtScale, lookAtZ);
          
          if (controls.current) {
            controls.current.enabled = false;
          }
  
          if (progress > 0.9) {
            const endEase = easeInOutCubic((progress - 0.9) / 0.1);
            camera.position.y *= (1 - endEase * 0.5);
          }
        } else {
          setIsRotating(false);
          if (controls.current) {
            controls.current.enabled = true;
          }
          setModelAnimationComplete();
          wanderTime.current = 0; // Initialize wandering time
        }
      } else if (!isRotating && hasAnimated.current) {
        // Subtle camera wandering after main animation
        wanderTime.current += delta;
        
        // Create a 30-second loop
        const loopProgress = (wanderTime.current % 30) / 30;
        
        // Multiple sine waves for organic motion
        const radiusOffset = Math.sin(loopProgress * Math.PI * 2) * 0.3;
        const heightOffset = Math.sin(loopProgress * Math.PI * 4) * 0.15 + 
                            Math.sin(loopProgress * Math.PI * 2) * 0.1;
        const angleOffset = Math.sin(loopProgress * Math.PI * 3) * 0.1;
        
        // Calculate new camera position
        const currentAngle = Math.atan2(camera.position.z, camera.position.x);
        const newAngle = currentAngle + angleOffset * delta;
        const newRadius = baseRadius + radiusOffset;
        
        // Smoothly interpolate to new position
        camera.position.x += (newRadius * Math.cos(newAngle) - camera.position.x) * 0.02;
        camera.position.z += (newRadius * Math.sin(newAngle) - camera.position.z) * 0.02;
        camera.position.y += (heightOffset - camera.position.y) * 0.02;
        
        // Subtle look-at point movement
        const lookAtX = Math.sin(loopProgress * Math.PI * 2) * 0.05;
        const lookAtZ = Math.cos(loopProgress * Math.PI * 2) * 0.05;
        camera.lookAt(lookAtX, heightOffset * 0.2, lookAtZ);
      }
    });
  
    return (
      <OrbitControls
        ref={controls}
        enableZoom={true}
        enablePan={false}
        rotateSpeed={0.5}
        minDistance={isPortrait ? mobileSettings.cameraZ - 4 : desktopSettings.cameraZ - 3}
        maxDistance={isPortrait ? mobileSettings.cameraZ + 4 : desktopSettings.cameraZ + 7}
        dampingFactor={0.05}
        enableDamping={true}
      />
    );
  }